import React, { useCallback, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IBaseItem, ICartEntry } from '@rbi-ctg/menu';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { createCartEntry } from 'utils/cart';
import { routes } from 'utils/routing';

import CartItem from '../cart-item/cart-item';
import SectionHeading from '../section-heading';

import { UpsellContainer } from './styles';
import { IUpsellProps } from './types';
import { UpsellSkeleton } from './upsell.skeleton';

const CartItems = styled.div`
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  display: flex;
  flex-direction: row;
`;

export const UpsellLoadingContainer = styled.div`
  min-height: 100px;
  display: flex;
`;

export const Upsell: React.FC<IUpsellProps> = ({
  hasBorderAtCheckout,
  items,
  loading,
  recommender,
  recommendationToken,
}) => {
  const { logUpsellAddedEvent } = useMParticleContext();
  const { pricingFunction } = useMenuContext();
  const { addToCart } = useOrderContext();
  const { formatMessage } = useIntl();

  const [orderAddedAlertMessage, setOrderAddedAlertMessage] = useState('');

  const handleAddToCart = useCallback(
    (item: IBaseItem) => {
      const price = pricingFunction(item);
      const entry: ICartEntry = {
        ...createCartEntry({
          item,
          price,
          location: { pathname: `${routes.menu}/${item._type}-${item._id}` },
        }),
        isUpsell: true,
        recommendationToken,
        recommender,
      };

      addToCart(entry);

      const cartItemIndex = items.findIndex(cartItem => cartItem._id === item._id);
      const cartItemPosition = cartItemIndex >= 0 ? cartItemIndex + 1 : undefined;
      logUpsellAddedEvent(entry, cartItemPosition);

      setOrderAddedAlertMessage(
        formatMessage({ id: 'itemAddedToCart' }, { itemName: item.name?.locale })
      );
    },
    [
      addToCart,
      formatMessage,
      items,
      logUpsellAddedEvent,
      pricingFunction,
      recommendationToken,
      recommender,
    ]
  );

  if (!items.length && !loading) {
    return null;
  }

  return (
    <UpsellContainer data-testid="upsell" hasBorderAtCheckout={hasBorderAtCheckout}>
      <SectionHeading id="add-to-order-header">
        {formatMessage({ id: 'addToOrderHeader' })}
      </SectionHeading>
      {loading && !items.length ? (
        <UpsellLoadingContainer>
          <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
          <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
        </UpsellLoadingContainer>
      ) : (
        <CartItems aria-labelledby="add-to-order-header">
          {items.map(item => (
            <CartItem
              key={item._id}
              item={item}
              addToCard={handleAddToCart}
              logEventData={{
                logEventName: CustomEventNames.UPSELL_DISPLAYED,
              }}
            />
          ))}
        </CartItems>
      )}
      {orderAddedAlertMessage && (
        <VisuallyHidden role="alert">{orderAddedAlertMessage}</VisuallyHidden>
      )}
    </UpsellContainer>
  );
};
